<template>
  <div>
    <CModal
      :show.sync="showModal"
      :close-on-backdrop="false"
      size="lg"
      title="Register Payment"
      @update:show="value => !value && closeModal()"
    >
      <CForm>
        <CRow
          v-if="!accountId"
          form
          class="form-group"
        >
          <CCol sm="3">
            <label>Buyer Account</label>
          </CCol>
          <CCol sm="9">
            <v-select
              v-model="form.accountId"
              :options="accountOptions"
              :reduce="option => option.value"
              label="label"
              placeholder="Search by account name or ID..."
              :filterable="true"
              :class="{ 'is-invalid': !form.accountId && form.isLoading }"
            >
              <template #no-options>
                No buyer accounts found
              </template>
            </v-select>
            <small
              v-if="!form.accountId && showAccountValidation"
              class="form-text text-danger"
            >
              Please select a buyer account
            </small>
          </CCol>
        </CRow>

        <CRow
          v-if="accountId && accountName"
          form
          class="form-group"
        >
          <CCol sm="3">
            <label>Account</label>
          </CCol>
          <CCol>
            <strong>{{ accountName }}</strong>
          </CCol>
        </CRow>

        <CRow
          v-if="calculatedOutstandingBalance !== null && form.paymentType !== 'balance'"
          form
          class="form-group"
        >
          <CCol sm="3">
            <label>Outstanding Balance</label>
          </CCol>
          <CCol>
            <strong class="text-danger">{{ formatAmount(calculatedOutstandingBalance) }}</strong>
            <small class="text-muted d-block">Total amount due from all unpaid invoices</small>
          </CCol>
        </CRow>

        <CRow
          v-if="oldestUnpaidInvoice && form.paymentType !== 'balance'"
          form
          class="form-group"
        >
          <CCol sm="3">
            <label>Oldest Unpaid Invoice</label>
          </CCol>
          <CCol>
            <div class="d-flex align-items-center">
              <strong class="text-warning mr-2">{{ oldestUnpaidInvoice.invoiceId }}</strong>
              <CBadge
                :color="oldestUnpaidInvoice.daysOverdue > 0 ? 'danger' : 'warning'"
                class="mr-2"
              >
                {{ oldestUnpaidInvoice.daysOverdue > 0 ? 'Overdue' : 'Outstanding' }}
              </CBadge>
              <span>{{ formatAmount(oldestUnpaidInvoice.remainingBalance) }}</span>
            </div>
            <small class="text-muted d-block">
              Due: {{ formatDate(oldestUnpaidInvoice.dueDate) }}
              <span v-if="oldestUnpaidInvoice.amountPaid > 0">
                ({{ formatAmount(oldestUnpaidInvoice.amountPaid) }} already paid)
              </span>
            </small>
            <small class="text-muted d-block">Payment will be applied to this invoice first</small>
          </CCol>
        </CRow>

        <CRow
          form
          class="form-group"
        >
          <CCol sm="3">
            <label>Payment Amount</label>
          </CCol>
          <CCol>
            <div class="input-group currency-input-wrapper">
              <input
                v-model="form.paymentAmountInCents"
                v-money="moneyConfig"
                type="text"
                :class="{
                  'form-control': true,
                  'currency-input': true,
                  'is-invalid': $v.form.paymentAmount.$dirty && $v.form.paymentAmount.$invalid
                }"
                placeholder="0.00"
                @input="onAmountChange"
                @blur="$v.form.paymentAmount.$touch()"
              >
            </div>
            <small
              v-if="$v.form.paymentAmount.$dirty && !$v.form.paymentAmount.required"
              class="form-text text-danger"
            >
              Payment amount is required
            </small>
            <small
              v-else-if="$v.form.paymentAmount.$dirty && !$v.form.paymentAmount.minValue"
              class="form-text text-danger"
            >
              Payment amount must be at least $0.01
            </small>
          </CCol>
        </CRow>

        <CRow
          form
          class="form-group"
        >
          <CCol sm="3">
            <label>Payment Type</label>
          </CCol>
          <CCol>
            <div class="d-flex flex-column">
              <CInputRadioGroup
                :options="paymentTypeOptions"
                :checked.sync="form.paymentType"
                custom
                inline
                class="mb-1"
              />
              <small class="text-muted">
                <template v-if="form.paymentType === 'single'">
                  Apply payment to the oldest unpaid invoice only
                </template>
                <template v-else-if="form.paymentType === 'cascade'">
                  Apply payment across multiple invoices if amount exceeds the oldest invoice
                </template>
                <template v-else-if="form.paymentType === 'balance'">
                  Add funds directly to account balance without paying any invoice
                </template>
              </small>
            </div>
          </CCol>
        </CRow>

        <CTextarea
          v-model="form.description"
          label="Description"
          placeholder="Enter payment description"
          horizontal
          rows="3"
          :class="{ 'is-invalid': $v.form.description.$dirty && $v.form.description.$invalid }"
          @blur="$v.form.description.$touch()"
        />
        <CInvalidFeedback
          v-if="$v.form.description.$dirty && !$v.form.description.required"
        >
          Description is required
        </CInvalidFeedback>
      </CForm>

      <template #footer>
        <ButtonLoading
          color="primary"
          :loading="form.isLoading"
          :disabled="!canSubmit"
          @click="onSubmit"
        >
          {{ form.paymentType === 'balance' ? 'Add Funds' : 'Submit Payment' }}
        </ButtonLoading>
        <CButton
          color="secondary"
          @click="closeModal"
        >
          Cancel
        </CButton>
      </template>
    </CModal>

    <CModal
      title="Please login again to verify its you"
      color="primary"
      :show.sync="showLoginModal"
    >
      <LoginInternal
        v-if="showLoginModal"
        @success="onLoginSuccess"
        @error="onLoginFailed"
      />

      <template #footer-wrapper>
        <div />
      </template>
    </CModal>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { required, minValue } from 'vuelidate/lib/validators'
import { formatAmount, formatDate } from '@/utils'
import { showErrorMessage, showSuccessMessage } from '@/notification-utils'
import LoginInternal from '@/views/Login/LoginInternal.vue'

export default {
  name: 'RegisterPaymentModal',

  components: {
    LoginInternal
  },

  props: {
    show: {
      type: Boolean,
      default: false
    },
    accountId: {
      type: String,
      default: ''
    },
    accountName: {
      type: String,
      default: ''
    },
    accountOptions: {
      type: Array,
      default: () => []
    },
    outstandingBalance: {
      type: Number,
      default: null
    }
  },

  validations() {
    return {
      form: {
        paymentAmount: {
          required,
          minValue: minValue(0.01)
        },
        description: {
          required
        }
      }
    }
  },

  data() {
    return {
      form: {
        accountId: '',
        paymentAmount: null,
        paymentAmountInCents: null,
        paymentType: 'cascade',
        description: '',
        isLoading: false
      },
      paymentTypeOptions: [
        { value: 'single', label: 'Single Invoice' },
        { value: 'cascade', label: 'Cascade' },
        { value: 'balance', label: 'Add to Balance' }
      ],
      oldestUnpaidInvoice: null,
      showAccountValidation: false,
      showLoginModal: false,
      moneyConfig: {
        decimal: '.',
        thousands: ',',
        suffix: '',
        precision: 2,
        masked: false,
        allowBlank: false,
        prefix: '$ ',
        min: 0.01
      }
    }
  },

  computed: {
    showModal: {
      get() {
        return this.show
      },
      set(value) {
        this.$emit('update:show', value)
      }
    },

    ...mapGetters('auth', [
      'isUserLoggedInWithinLastXMinutes'
    ]),

    isUserLoggedInWithinLast60Minutes() {
      return this.isUserLoggedInWithinLastXMinutes(60)
    },

    resolvedAccountId() {
      return this.accountId || this.form.accountId
    },

    calculatedOutstandingBalance() {
      return this.outstandingBalance
    },

    canSubmit() {
      return (
        this.resolvedAccountId &&
        !this.$v.form.paymentAmount.$invalid &&
        !this.$v.form.description.$invalid &&
        !this.form.isLoading
      )
    }
  },

  watch: {
    show(newVal) {
      if (newVal && this.accountId) {
        this.form.accountId = this.accountId
        this.loadOutstandingBalance()
      }
    },

    'form.accountId'(newValue) {
      if (newValue) {
        this.showAccountValidation = false
      }
    }
  },

  methods: {
    formatAmount,
    formatDate,

    ...mapActions('billing', [
      'registerGenericPayment',
      'getAccountOutstandingBalance',
      'generateCollectionStatement'
    ]),

    ...mapActions('accounts', [
      'addFundsToBuyerAccount'
    ]),

    ...mapActions('auth', [
      'logout'
    ]),

    async loadOutstandingBalance() {
      if (!this.resolvedAccountId) {
        return
      }

      try {
        if (this.outstandingBalance === null) {
          const balance = await this.getAccountOutstandingBalance(this.resolvedAccountId)
          this.$emit('update:outstandingBalance', balance)
        }
      } catch {
        // Outstanding balance is optional, don't block
      }

      try {
        const statement = await this.generateCollectionStatement({ accountId: this.resolvedAccountId })
        const invoices = (statement?.invoices || [])
          .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate))
        this.oldestUnpaidInvoice = invoices.length > 0 ? invoices[0] : null
      } catch {
        // Oldest invoice is optional, don't block
      }
    },

    onAmountChange() {
      if (this.form.paymentAmountInCents) {
        let valueInCents
        if (typeof this.form.paymentAmountInCents === 'string') {
          const cleaned = this.form.paymentAmountInCents.replace(/[$,\s]/g, '')
          valueInCents = parseFloat(cleaned) * 100
        } else {
          valueInCents = this.form.paymentAmountInCents
        }

        this.form.paymentAmount = valueInCents / 100
        this.$v.form.paymentAmount.$touch()
      } else {
        this.form.paymentAmount = null
      }
    },

    async onSubmit() {
      this.$v.form.$touch()

      if (!this.resolvedAccountId) {
        this.showAccountValidation = true
      }

      if (this.$v.form.$invalid || !this.resolvedAccountId) {
        const errors = []

        if (!this.resolvedAccountId) {
          errors.push('Buyer account must be selected')
        }

        if (this.$v.form.paymentAmount.$invalid) {
          if (!this.$v.form.paymentAmount.required) {
            errors.push('Payment amount is required')
          } else if (!this.$v.form.paymentAmount.minValue) {
            errors.push('Payment amount must be at least $0.01')
          }
        }

        if (this.$v.form.description.$invalid) {
          errors.push('Description is required')
        }

        showErrorMessage(errors.length > 0
          ? `Validation errors: ${errors.join(', ')}`
          : 'Please fix validation errors before submitting')
        return
      }

      this.showAccountValidation = false

      if (!this.isUserLoggedInWithinLast60Minutes) {
        this.showLoginModal = true
        this.showModal = false
        return
      }

      this.form.isLoading = true

      try {
        if (this.form.paymentType === 'balance') {
          await this.addFundsToBuyerAccount({
            accountId: this.resolvedAccountId,
            amount: this.form.paymentAmount,
            notes: this.form.description
          })
          showSuccessMessage('Funds have been successfully added to account balance.')
        } else {
          await this.registerGenericPayment({
            accountId: this.resolvedAccountId,
            paymentAmount: this.form.paymentAmount,
            applyCascade: this.form.paymentType === 'cascade',
            description: this.form.description
          })
          showSuccessMessage('Payment has been successfully registered.')
        }

        this.closeModal()
        this.$emit('payment-registered')
      } catch (error) {
        showErrorMessage(error)
      } finally {
        this.form.isLoading = false
      }
    },

    closeModal() {
      this.showModal = false
      this.showAccountValidation = false
      this.oldestUnpaidInvoice = null
      this.form = {
        accountId: '',
        paymentAmount: null,
        paymentAmountInCents: null,
        paymentType: 'cascade',
        description: '',
        isLoading: false
      }
      this.$v.form.$reset()
    },

    onLoginSuccess() {
      this.showLoginModal = false
      this.showModal = true
    },

    onLoginFailed() {
      try {
        this.logout()
      } catch {
        //
      } finally {
        this.$router.push({ name: 'LoginEmail' })
      }
    }
  }
}
</script>
